import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Card,
  ConfigProvider,
  Dropdown,
  Flex,
  Tag,
  theme,
  Tooltip,
  Typography,
} from "antd";
import { memo, useContext, useMemo, useState } from "react";
import trans, { formatDuration } from "../../../utils";
import { popupcontext } from "../../../context/Popupscontext";
import { Input } from "../../Helpers/Input/Input";
const { Text } = Typography;

// eslint-disable-next-line no-unused-vars
const KanbanCard = ({
  id,
  dueDate,
  assignedTo,
  code = "Task-003",
  priority = "Medium",
  updatedAt,
  users = [],
  onAssign,
  isDropdownOpen,
  setIsDropdownOpen,
  notes,
  expectedDuration,
  status = "unAssigned",
  name,
  poi,
  onView,
  onDelete,
  onEdit,
  isDelayed,
  isOverDue,
  canEdit,
  canDelete,
}) => {
  const { token } = theme.useToken();
  const { handleShow } = useContext(popupcontext);
  const [search, setSearch] = useState("");

  const filteredUsers = users.filter((user) =>
    user.name.toLowerCase().includes(search.toLowerCase())
  );

  const statusFlagMap = {
    todo: `${process.env.PUBLIC_URL}/assets/flag-blue.svg`,
    inProgress: `${process.env.PUBLIC_URL}/assets/flag-orange.svg`,
    done: `${process.env.PUBLIC_URL}/assets/flag-green.svg`,
    // delayed: `${process.env.PUBLIC_URL}/assets/flag-red.svg`,
    unassigned: `${process.env.PUBLIC_URL}/assets/flag-default.svg`,
  };

  const priorityColors = {
    low: `green`,
    medium: `orange`,
    high: `red`,
  };

  const dropdownItems = useMemo(() => {
    const items = [
      {
        label: trans("View task"),
        key: 1,
        icon: <EyeOutlined />,
        onClick: () => {
          onView();
        },
      },
    ];

    if (canEdit === undefined || canEdit === true) {
      items.push({
        label: trans("Edit task"),
        key: 2,
        icon: <EditOutlined />,
        onClick: () => {
          onEdit();
        },
      });
    }

    if (canDelete === undefined || canDelete === true) {
      items.push({
        danger: true,
        label: trans("Delete Task"),
        key: 3,
        icon: <DeleteOutlined />,
        onClick: () => {
          handleShow("deleteItem", "task", id);
          onDelete();
        },
      });
    }

    return items;
  }, [canEdit, canDelete, id, onDelete, onEdit, onView, handleShow]);

  const dropdownContent = (
    <div
      style={{ width: 250, backgroundColor: "#fff" }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Search Bar */}
      <div style={{ padding: 8 }}>
        <Input
          placeholder="Search user"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
      </div>

      {/* Scrollable User List */}
      <div style={{ maxHeight: 200, overflowY: "auto" }}>
        {filteredUsers.map((user) => {
          const isSelected = assignedTo?.id === user.id;

          return (
            <Flex
              key={user.id}
              align="center"
              gap={8}
              style={{
                padding: "6px 12px",
                cursor: "pointer",
                transition: "background 0.2s",
                background: isSelected ? "#f0faf4" : "transparent",
                borderLeft: isSelected
                  ? "3px solid #0f6936"
                  : "3px solid transparent",
              }}
              onClick={() => onAssign?.(id, user)}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f5f5f5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <Avatar
                size="small"
                src={user?.avatar}
                icon={!user?.avatar && <UserOutlined />}
              />
              {user.name}
            </Flex>
          );
        })}

        {/* Unassign Option */}
        <Flex
          align="center"
          gap={8}
          style={{
            padding: "6px 12px",
            cursor: "pointer",
            color: "red",
          }}
          onClick={() => onAssign?.(id, null)}
        >
          <Avatar size="small" icon={<UserOutlined />} />
          Unassign
        </Flex>
      </div>
    </div>
  );

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#0f6936",
        },
        components: {
          Tag: {
            colorText: token.colorTextSecondary,
          },
          Card: {
            headerBg: "transparent",
          },
        },
      }}
    >
      <Card size="small">
        <Flex justify="space-between" style={{ marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <Flex align="center" gap={4}>
              <img
                src={statusFlagMap[status] || statusFlagMap.unassigned}
                alt={status}
                style={{ width: 14, height: 14 }}
              />
              <Text
                strong
                style={{ fontSize: 12, color: "#000000E0", fontWeight: 300 }}
              >
                {code}
              </Text>
            </Flex>

            {/* Second row: tags */}
            <Flex gap={4} wrap="wrap" style={{ marginTop: 4 }}>
              <Tag
                color={priorityColors[priority] || priorityColors.medium}
                className="text-capitalize"
              >
                {trans(priority)}
              </Tag>

              {isDelayed && (
                <Tooltip
                  title={trans(
                    "The actual start time was later than the scheduled start time"
                  )}
                >
                  <Tag color="red" className="text-capitalize">
                    {trans("delayed")}
                  </Tag>
                </Tooltip>
              )}

              {isOverDue && (
                <Tooltip
                  title={trans("The work exceeded the estimated duration")}
                >
                  <Tag color="red-inverse" className="text-capitalize">
                    {trans("Over Due")}
                  </Tag>
                </Tooltip>
              )}
            </Flex>
          </div>
          {/* <Flex align="center" gap={4}>
            <img
              src={statusFlagMap[status] || statusFlagMap.unassigned}
              alt={status}
              style={{ width: 14, height: 14 }}
            />
            <Text
              strong
              style={{ fontSize: 12, color: "#000000E0", fontWeight: 300 }}
            >
              {code}
            </Text>
            <Tag
              color={priorityColors[priority] || priorityColors.medium}
              className="text-capitalize"
            >
              {trans(priority)}
            </Tag>
            {isDelayed && (
              <Tooltip
                title={trans(
                  "The actual start time was later than the scheduled start time"
                )}
              >
                <Tag color={"red"} className="text-capitalize">
                  {trans("delayed")}
                </Tag>
              </Tooltip>
            )}
            {isOverDue && (
              <Tooltip
                title={trans("The work exceeded the estimated duration")}
              >
                <Tag color={"red-inverse"} className="text-capitalize">
                  {trans("Over Due")}
                </Tag>
              </Tooltip>
            )}
          </Flex> */}
          <Dropdown
            trigger={["click"]}
            menu={{
              items: dropdownItems,
              onClick: (e) => {
                e.domEvent.stopPropagation();
              },
            }}
            onOpenChange={setIsDropdownOpen}
            placement="bottomRight"
          >
            <Button
              type="default"
              icon={<MoreOutlined rotate={90} />}
              size="small"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </Dropdown>
        </Flex>
        <div className="task-title text-capitalize">{name}</div>

        <div className="task-description">{notes}</div>

        {/* Meta: Location and Duration */}
        <Flex gap={12} style={{ marginBottom: 12 }}>
          {poi && (
            <Tooltip title={trans("POI Name")}>
              <Flex align="center" gap={6}>
                <img
                  src={`${process.env.PUBLIC_URL}/assets/building.svg`}
                  alt="building"
                />
                <Text style={{ fontSize: 13 }}>{poi?.name}</Text>
              </Flex>
            </Tooltip>
          )}
          {expectedDuration && (
            <Tooltip title={trans("Duration")}>
              <Flex align="center" gap={6}>
                <img
                  src={`${process.env.PUBLIC_URL}/assets/alarm-clock.svg`}
                  alt="time"
                />
                <Text style={{ fontSize: 13 }}>
                  {formatDuration(expectedDuration)}
                </Text>
              </Flex>
            </Tooltip>
          )}
        </Flex>

        {/* Footer: Assigned User */}
        <Flex justify="flex-end">
          <Dropdown
            trigger={["click"]}
            dropdownRender={() => dropdownContent}
            onOpenChange={setIsDropdownOpen}
            placement="topRight"
            disabled={!canEdit}
          >
            <Tooltip title={assignedTo?.name || trans("Unassigned")}>
              <Avatar
                size={32}
                src={assignedTo?.avatar}
                icon={
                  !assignedTo?.avatar && (
                    <UserOutlined
                      style={{
                        color: "#0f6936",
                        fontSize: "inherit",
                      }}
                    />
                  )
                }
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  cursor: "pointer",
                  border: "1px solid #BEDDC5",
                  boxSizing: "border-box",
                  backgroundColor: "#fff",
                  color: "#0f6936",
                }}
              />
            </Tooltip>
          </Dropdown>
        </Flex>
      </Card>
    </ConfigProvider>
  );
};

export default KanbanCard;

export const KanbanCardMemo = memo(KanbanCard, (prev, next) => {
  return (
    prev.id === next.id &&
    prev.name === next.name &&
    prev.dueDate === next.dueDate &&
    prev.assignedTo === next.assignedTo &&
    prev.updatedAt === next.updatedAt &&
    prev.users === next.users
  );
});
